import type { ReactNode } from 'react';

/**
 * Conteneur générique d'un item :
 * - `fieldset` + `legend` sémantique (RGAA).
 * - Hint (texte d'aide) lié par `aria-describedby`.
 * - Message d'erreur (aria-live=polite, icône texte) — jamais couleur seule.
 *
 * `legend` est toujours visible (jamais sr-only) : la question fait partie
 * intégrante de la conversation avec le répondant.
 */
interface ItemShellProps {
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
  /** Pour les `select_one` / `select_multiple` qui sont déjà un radiogroup. */
  role?: 'radiogroup' | 'group';
}

export function ItemShell({
  name,
  label,
  hint,
  required,
  error,
  children,
  role,
}: ItemShellProps) {
  const hintId = hint ? `${name}-hint` : undefined;
  const errorId = error ? `${name}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <fieldset
      id={`item-${name}`}
      className="space-y-3"
      aria-describedby={describedBy}
      aria-required={required || undefined}
      aria-invalid={error ? true : undefined}
      role={role}
    >
      <legend className="field-label text-lg leading-snug max-w-prose">
        {label}
        {required && (
          <span className="text-text-muted text-sm font-normal ml-1" aria-hidden="true">
            (requis)
          </span>
        )}
      </legend>
      {hint && (
        <p id={hintId} className="field-hint max-w-prose">
          {hint}
        </p>
      )}
      <div>{children}</div>
      {error && (
        <p
          id={errorId}
          className="field-error"
          role="alert"
          aria-live="polite"
        >
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </p>
      )}
    </fieldset>
  );
}
